import { useEffect, useMemo, useRef, useState } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from "react-native";
import Svg, { Circle, Path } from "react-native-svg";

import { cadenceMs, freshness, isGap, type Point } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";

// Interactive line chart shared across the measurement / metabolic trends and
// the Progress panels (S3 lift + Progress follow-up, 2026-06-29).
//
// Design (Antenore): lines are NEUTRAL — brand orange is reserved for the
// FOCAL e1RM series (its own `focal` variant) — except the latest point of a
// neutral primary series, which stays orange as the "now" accent (mirrors the
// orange last-week bar in the Progress frequency chart). A secondary curve
// (e.g. a Health-Connect-imported value) is a muted dashed line. Every series
// carries a readable CURRENT value in the meta row (so a dashed companion
// curve shows its number, his main pain), each next to a solid/dashed swatch.
// Tapping any point reveals a dark mono tooltip with that point's date + value.
//
// Variants:
//  - primary   solid `ink` line, last dot `brand500` ("now")
//  - focal     solid `brand500` line (the e1RM exception) — colour-budget KEEP
//  - secondary muted dashed `ink3` line (no "now" dot)
//
// HM-1 (2026-07-15) — THE X AXIS IS TIME, NOT THE ARRAY INDEX. It used to be
// `PAD_X + i * stepX`: every call site passed a real instant in `Point.x` and
// this file never read it. Surviving points re-stretched to fill the width, so
// 25 days of readings plus a 5-day hole drew exactly like 30 full days — a hole
// wasn't drawn, it was erased — and the orange "now" dot was by construction
// the last array element, so the app asserted that a five-day-old reading was
// the present. That is how the CGM pipeline's death (10-07 20:31) stayed
// invisible for five days. Three rules now:
//  1. x comes from `Point.x`, on an axis that runs to `nowMs` — a series that
//     stopped ends where it stopped, and the emptiness after it IS the fault.
//  2. the path BREAKS across a silence longer than the series' own rhythm, so
//     a hole is never interpolated across (`isGap`, shared/freshness.ts).
//  3. the orange dot means "now", so it is gated on the last point actually
//     being recent — never on it being last.
// Thresholds are DERIVED from each series' own cadence, never declared: this
// component cannot know whether a glucose series is a 5-minute CGM or a
// hand-typed reading, and the answer changes week to week (Antenore, 2026-07-15).
// `MiniTrendBars` is the correct sibling for CATEGORICAL series (weekly
// buckets, where index IS time) — it is not affected by any of this.

export type ChartSeries = {
  key: string;
  points: Point[];
  variant?: "primary" | "secondary" | "focal";
};

type Props = {
  series: ChartSeries[];
  theme: UiTheme;
  yFormat?: (y: number) => string;
  height?: number;
  /** Show the range + per-series current-value footer. Off for compact
   *  per-row sparklines where the current value is already shown beside the
   *  chart (e.g. the E1RM trends panel). Default on. */
  showMeta?: boolean;
  /** Right edge of the time axis. The axis runs [oldest point → now], so a
   *  series that stopped days ago visibly ends short of the edge instead of
   *  re-stretching over its own silence. Defaults to render time; hosts that
   *  already hold a clock (MetabolicCard) pass it so the value is stable. */
  nowMs?: number;
};

const VBW = 320; // viewBox width; the host View stretches it to fill the row.
const PAD_X = 8;
const PAD_Y = 12;
const TOOLTIP_MS = 4000;
const BUBBLE_W = 132;

type Dot = { vx: number; vy: number; value: number; label?: string };
type Active = { sKey: string; i: number };
/** A run of points with no hole in it — one <Path>. A series with a silence in
 *  the middle draws as several, so the silence stays empty. */
type Segment = { d: string };

export function LineChart({
  series,
  theme,
  yFormat = (n) => n.toFixed(0),
  height = 80,
  showMeta = true,
  nowMs,
}: Props) {
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<Active | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const computed = useMemo(() => {
    const allYs = series.flatMap((s) => s.points.map((p) => p.y));
    if (allYs.length === 0) return null;
    const innerH = height - PAD_Y * 2;
    const innerW = VBW - PAD_X * 2;
    let lo = Math.min(...allYs);
    let hi = Math.max(...allYs);
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const ySpan = hi - lo;
    const toVY = (y: number) => PAD_Y + (1 - (y - lo) / ySpan) * innerH;

    // ONE time axis for every series (a dashed companion curve must line up
    // with the solid one it annotates): oldest sample → now. Ending the axis at
    // `now` rather than at the newest sample is what makes a dead feed visible
    // — the emptiness on the right is the silence, drawn to scale.
    const now = nowMs ?? Date.now();
    const allXs = series.flatMap((s) => s.points.map((p) => p.x));
    const x0 = Math.min(...allXs);
    const xSpan = Math.max(1, Math.max(now, ...allXs) - x0);
    const toVX = (x: number) => PAD_X + ((x - x0) / xSpan) * innerW;

    const built = series.map((s) => {
      const variant = s.variant ?? "primary";
      const xs = s.points.map((p) => p.x);
      const cadence = cadenceMs(xs);

      // Break the path across any silence longer than this series' own habit.
      // Without this the hole is a straight line asserting values nobody ever
      // measured — the axis would be honest and the ink still lying.
      const segments: Segment[] = [];
      const dots: Dot[] = [];
      let d = "";
      s.points.forEach((p, i) => {
        const vx = toVX(p.x);
        const vy = toVY(p.y);
        const prev = i > 0 ? s.points[i - 1] : undefined;
        const broken = prev !== undefined && isGap(p.x - prev.x, cadence);
        if (broken && d) {
          segments.push({ d });
          d = "";
        }
        d += d === "" ? `M ${vx.toFixed(1)} ${vy.toFixed(1)}` : ` L ${vx.toFixed(1)} ${vy.toFixed(1)}`;
        dots.push({ vx, vy, value: p.y, label: p.label });
      });
      if (d) segments.push({ d });

      // "Now" is a claim about time, so it is EARNED by being recent — never by
      // being last, and never by default.
      //
      // Three states, not two (Antenore, 2026-07-15 — «niente arancione senza
      // prove»). Unknown cadence (too few points to have a rhythm) is its own
      // answer: the accent is withheld, but the dot does NOT go hollow either,
      // because we cannot call a datum stale when we don't know what its normal
      // looks like. Letting "unknown" keep the accent was the tempting default
      // and it rebuilt this very bug in miniature: from ~25-08 the dead CGM's
      // readings age out of the window, only a handful of hand-typed ones
      // remain, and a month-old reading would have taken the orange back.
      const fresh = freshness(xs, now);
      const stale = fresh.known && fresh.stale;
      const current = fresh.known && !fresh.stale;

      const last = s.points[s.points.length - 1]?.y ?? 0;
      return { key: s.key, variant, segments, dots, last, stale, current };
    });
    return { lo, hi, built };
  }, [series, height, nowMs]);

  if (!computed) return null;

  // viewBox→pixel: preserveAspectRatio="none" stretches x to the measured
  // width and maps y 1:1 (the Svg height equals the viewBox height).
  const toPX = (vx: number) => (width > 0 ? (vx / VBW) * width : 0);

  const tap = (sKey: string, i: number) => {
    if (timer.current) clearTimeout(timer.current);
    setActive((prev) => {
      if (prev && prev.sKey === sKey && prev.i === i) return null;
      timer.current = setTimeout(() => setActive(null), TOOLTIP_MS);
      return { sKey, i };
    });
  };

  const activeDot = active
    ? computed.built.find((b) => b.key === active.sKey)?.dots[active.i]
    : undefined;

  const lineColor = (variant: string) =>
    variant === "focal" ? theme.palette.brand500 : theme.palette.ink;

  return (
    <View style={styles.wrap}>
      <View style={[styles.plot, { height }]} onLayout={onLayout}>
        <Svg viewBox={`0 0 ${VBW} ${height}`} preserveAspectRatio="none" width="100%" height={height}>
          {computed.built.flatMap((b) =>
            b.segments.map((seg, si) =>
              b.variant === "secondary" ? (
                <Path
                  key={`${b.key}-s${si}`}
                  d={seg.d}
                  fill="none"
                  stroke={theme.palette.ink3}
                  strokeWidth={1.25}
                  strokeDasharray="3,3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ) : (
                <Path
                  key={`${b.key}-s${si}`}
                  d={seg.d}
                  fill="none"
                  stroke={lineColor(b.variant)}
                  strokeWidth={2.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ),
            ),
          )}
          {computed.built.flatMap((b) =>
            b.dots.map((dot, i) => {
              const isLast = i === b.dots.length - 1;
              // Positive evidence of currency, not merely absence of staleness.
              const isNowDot = b.variant === "primary" && isLast && b.current;
              // A stale last point stays VISIBLE but goes hollow: it is the
              // newest reading we have, so hiding it would be a second lie —
              // it just stops claiming to be the present. Hollow-vs-solid is a
              // SHAPE difference, so the cue survives colour-blindness; it is
              // drawn a size up (5 vs 4, ring 2.5) because a 2px ring inside
              // r=4 is a fragile read at a between-sets glance, and legibility
              // bounds this design.
              const isStaleDot = b.variant === "primary" && isLast && b.stale;
              return (
                <Circle
                  key={`${b.key}-${i}`}
                  cx={dot.vx}
                  cy={dot.vy}
                  r={b.variant === "secondary" ? 2.5 : isStaleDot ? 5 : isLast ? 4 : 3}
                  fill={
                    b.variant === "secondary"
                      ? theme.palette.ink3
                      : b.variant === "focal"
                        ? theme.palette.brand500
                        : isNowDot
                          ? theme.palette.brand500 // the "now" accent
                          : isStaleDot
                            ? theme.palette.surface
                            : theme.palette.ink
                  }
                  stroke={isStaleDot ? theme.palette.ink : undefined}
                  strokeWidth={isStaleDot ? 2.5 : undefined}
                />
              );
            }),
          )}
        </Svg>

        {/* Transparent tap targets over each point — placed in pixel space
            (needs the measured width). One frame after layout. */}
        {width > 0
          ? computed.built.flatMap((b) =>
              b.dots.map((dot, i) => (
                <Pressable
                  key={`hit-${b.key}-${i}`}
                  onPress={() => tap(b.key, i)}
                  accessibilityRole="button"
                  accessibilityLabel={`${dot.label ?? ""} ${yFormat(dot.value)}`.trim()}
                  style={[styles.hit, { left: toPX(dot.vx) - 13, top: dot.vy - 13 }]}
                />
              )),
            )
          : null}

        {activeDot ? (
          <View
            style={[
              styles.bubble,
              {
                left: Math.min(Math.max(toPX(activeDot.vx) - BUBBLE_W / 2, 0), Math.max(width - BUBBLE_W, 0)),
                top: activeDot.vy > height / 2 ? 0 : undefined,
                bottom: activeDot.vy > height / 2 ? undefined : 0,
              },
            ]}
            pointerEvents="none"
            accessibilityRole="text"
          >
            <Text style={styles.bubbleText}>
              {activeDot.label ? `${activeDot.label} · ` : ""}
              {yFormat(activeDot.value)}
            </Text>
          </View>
        ) : null}
      </View>

      {showMeta ? (
        <View style={styles.meta}>
          <Text style={styles.range}>{`${yFormat(computed.lo)} – ${yFormat(computed.hi)}`}</Text>
          <View style={styles.currents}>
            {computed.built
              .filter((b) => b.dots.length > 0)
              .map((b) => (
                <View key={`cur-${b.key}`} style={styles.current}>
                  <View
                    style={
                      b.variant === "secondary"
                        ? [styles.swatch, styles.swatchDashed]
                        : b.variant === "focal"
                          ? [styles.swatch, styles.swatchFocal]
                          : styles.swatch
                    }
                  />
                  <Text style={b.variant === "secondary" ? styles.curMuted : styles.curStrong}>
                    {yFormat(b.last)}
                  </Text>
                </View>
              ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t, radius: r, spacing: s } = theme;
  return StyleSheet.create({
    wrap: { width: "100%" },
    plot: { width: "100%", position: "relative" },
    hit: { position: "absolute", width: 26, height: 26 },
    bubble: {
      position: "absolute",
      width: BUBBLE_W,
      backgroundColor: p.ink,
      borderRadius: r.r2,
      paddingHorizontal: s.s2,
      paddingVertical: s.s1,
      zIndex: 20,
      elevation: 6,
      alignItems: "center",
    },
    bubbleText: {
      color: p.bg,
      fontSize: t.sizes.xs,
      fontWeight: "600",
      fontFamily: t.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    meta: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingTop: 2 },
    range: { color: p.ink3, fontSize: t.sizes.xs, fontVariant: ["tabular-nums"] },
    currents: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    current: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    swatch: { width: 14, height: 0, borderTopWidth: 2.5, borderTopColor: p.ink },
    swatchDashed: { borderTopWidth: 1.5, borderTopColor: p.ink3, borderStyle: "dashed" },
    swatchFocal: { borderTopColor: p.brand500 },
    curStrong: { color: p.ink, fontSize: t.sizes.xs, fontWeight: "700", fontVariant: ["tabular-nums"] },
    curMuted: { color: p.ink3, fontSize: t.sizes.xs, fontWeight: "600", fontVariant: ["tabular-nums"] },
  });
}
